import { APP_BASE_HREF, AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, inject, Injector, Input, NgZone, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PActionButtonModule } from '@panaya/ui/p-action-button';
import { PIconModule } from '@panaya/ui/p-icon';
import { PLinkModule } from '@panaya/ui/p-link';
import { ButtonTypeEnum, PButtonModule } from '@panaya/ui/p-button';
import { MarkdownComponent, MarkdownModule } from 'ngx-markdown';
import { AssistantMessage, ChatMessage, createUserPrompt, UserFeedback, UserMessage } from '@panaya/ai-chat/shared';
import { FeedbackMode, PLikeDislikeModule } from '@panaya/ui/p-like-dislike';
import { AiChatUiEventsService } from '../../../ai-chat-ui-events.service';
import { AiChatUserFeedbackInteractionComponent } from '../../../user-feedback-interaction';
import { map, merge, Observable, Subject } from 'rxjs';
import { PAttachmentPreviewComponent } from '@panaya/ui/p-attachment-preview';
import { AttachmentIconPipe } from '../../../attachments/attachment-icon.pipe';

@Component({
    standalone: true,
    selector: 'p-ai-chat-window-message',
    imports: [
        MarkdownModule,
        PIconModule,
        PLinkModule,
        PActionButtonModule,
        NgTemplateOutlet,
        NgClass,
        PButtonModule,
        PLikeDislikeModule,
        AiChatUserFeedbackInteractionComponent,
        AsyncPipe,
        TranslateModule,
        PAttachmentPreviewComponent,
        AttachmentIconPipe
    ],
    templateUrl: './ai-chat-window-message.component.html',
    styleUrls: ['./ai-chat-window-message.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatWindowMessageComponent {
    @Input() public linkPrefix?: string;
    @Input() public message?: ChatMessage;
    @Input() public assistantWorking = false;
    @ViewChild(MarkdownComponent) markdown!: MarkdownComponent;
    protected readonly buttonTypeEnum = ButtonTypeEnum;
    protected readonly showFeedbackInteraction$: Observable<boolean>;

    private readonly showFeedbackInteraction = new Subject<boolean>();
    private readonly uiEvents = inject(AiChatUiEventsService);
    private readonly router = inject(Router);
    private readonly ngZone = inject(NgZone);
    private readonly baseHref = inject(APP_BASE_HREF);

    private readonly injector = inject(Injector);

    constructor() {
        this.showFeedbackInteraction$ = merge(this.uiEvents.promptRequest.pipe(map(() => false)), this.showFeedbackInteraction);
    }

    protected isUserMessage(): this is { message: UserMessage } {
        return this.message?.source === 'user';
    }

    protected asUserExtras(extras: ChatMessage['extras']): UserMessage['extras'] {
        return extras as UserMessage['extras'];
    }

    protected asAssistantExtras(extras: ChatMessage['extras']): AssistantMessage['extras'] {
        return extras as AssistantMessage['extras'];
    }

    protected onMarkdownReady(): void {
        // Use afterNextRender to ensure DOM is fully rendered before processing links
        afterNextRender(
            () => {
                this.markdown?.element?.nativeElement
                    ?.querySelectorAll<HTMLAnchorElement>('a[href]')
                    ?.forEach((link) => this.fixContentLink(link));
            },
            { injector: this.injector }
        );
    }

    private fixContentLink(link: HTMLAnchorElement): void {
        const href = link.getAttribute('href');
        if (!href) {
            return;
        }
        if (this.isInAppLink(href)) {
            link.addEventListener('click', (event) => this.onInAppLinkClick(event, href));
        } else {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        }
    }

    private isInAppLink(href: string): boolean {
        // `seemore=true` is the marker the URL-builder MCP tool stamps on every link it
        // produces — same backend the chat library already references by name elsewhere
        // (see url-builder-thinking.interceptor.ts). Same-origin guards against a
        // wrong-environment URL slipping through into the router.
        try {
            const url = new URL(href, window.location.origin);
            return url.origin === window.location.origin && url.searchParams.get('seemore') === 'true';
        } catch {
            return false;
        }
    }

    private onInAppLinkClick(event: MouseEvent, href: string): void {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
            return;
        }
        event.preventDefault();
        const path = this.toInAppPath(href);
        if (path) {
            // This handler runs outside NgZone (registered in afterNextRender), so navigating
            // without ngZone.run() would leave the router-busy spinner stuck (no change detection).
            this.ngZone.run(() => this.router.navigateByUrl(path));
        }
    }

    private toInAppPath(href: string): string | null {
        try {
            const url = new URL(href, window.location.origin);
            let pathname = url.pathname;
            const base = this.baseHref?.replace(/\/$/, '') ?? '';
            if (base && pathname.startsWith(base)) {
                pathname = pathname.slice(base.length) || '/';
            }
            return pathname + url.search + url.hash;
        } catch {
            return null;
        }
    }

    protected triggerActionSuggestionPrompt(actionSuggestion: string): void {
        this.uiEvents.promptRequest.emit(createUserPrompt({ message: actionSuggestion, context: '' }));
    }

    protected likeDislikeChanged(feedbackMode: FeedbackMode, messageId?: string): void {
        const userFeedback: UserFeedback = { mode: feedbackMode };
        this.showFeedbackInteraction.next(feedbackMode === FeedbackMode.DISLIKE);
        this.submitFeedback(userFeedback, messageId);
    }

    protected feedbackReasonChanged(userFeedback: UserFeedback): void {
        this.submitFeedback(userFeedback);
    }

    protected feedbackTextSubmitted(userFeedback: UserFeedback): void {
        this.showFeedbackInteraction.next(false);
        this.submitFeedback(userFeedback);
    }

    private submitFeedback(userFeedback: UserFeedback, overrideMessageId?: string): void {
        const messageId = overrideMessageId || this.asAssistantExtras(this.message?.extras)?.messageId;
        if (messageId) {
            this.uiEvents.userFeedbackRequest.emit({ messageId, userFeedback });
        }
    }
}
